// src/hooks/usePermissions.ts
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PermissionService from '../PermissionService';
import {
  UsePermissionsReturn,
  UsePermissionsOptions,
  Permissions,
  CanMethods,
  PlanFeatures,
  Usage,
  EffectiveLimits,
  ActiveAddon,
  SubscriptionInfo,
  ServiceUsage,
  TeamUsage
} from '../types';

// Move default values outside the hook to avoid recreation
const emptyCanMethods: CanMethods = {
  addService: () => false,
  addTeamMember: () => false,
  useFeaturedListing: () => false,
  useAdvancedAnalytics: () => false,
  useBookingCalendar: () => false,
  useTwoWayMessaging: () => false,
  useCustomBranding: () => false,
  useAdvancedPromoTools: () => false,
  hasReachedServiceLimit: () => false,
  hasReachedTeamLimit: () => false,
  canUpgrade: () => false,
  getServiceUsagePercentage: () => 0,
  getTeamUsagePercentage: () => 0
};

const emptyFeatures: PlanFeatures = {
  analytics: 'none',
  bookingCalendar: false,
  messaging: 'none',
  branding: false,
  promoTools: 'none'
};

const emptyUsage: Usage = {
  servicesCount: 0,
  teamMembersCount: 0,
  featuredListingsCount: 0
};

const emptyLimits: EffectiveLimits = {
  maxServices: 0,
  maxTeamMembers: 0,
  maxFeaturedListings: 0,
  isUnlimited: false
};

const emptySubscription: SubscriptionInfo = {
  plan: '',
  tier: 0,
  status: '',
  billingCycle: '',
  currentPeriodEnd: '',
  stripeSubscriptionId: ''
};

const emptyAddons: ActiveAddon[] = [];

const usePermissions = (
  userId: string | null,
  serviceInstance: PermissionService,
  options: UsePermissionsOptions = {}
): UsePermissionsReturn => {
  const {
    autoLoad = true,
    refreshInterval = null,
    onError = null,
    onPermissionsLoaded = null
  } = options;

  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [loading, setLoading] = useState<boolean>(autoLoad);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadPermissions = useCallback(async (): Promise<Permissions | null> => {
    if (!userId || !serviceInstance) return null;

    try {
      setLoading(true);
      setError(null);
      
      const data = await serviceInstance.getPermissions(userId);
      setPermissions(data);
      
      if (onPermissionsLoaded) {
        onPermissionsLoaded(data);
      }
      
      return data;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load permissions';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      console.error('Error loading permissions:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, serviceInstance, onError, onPermissionsLoaded]);

  const trackUsage = useCallback(async (type: string, count: number = 1): Promise<void> => {
    if (!userId || !serviceInstance) return;

    try {
      await serviceInstance.trackUsage(userId, type, count);
      // Auto-refresh permissions after tracking
      await loadPermissions();
    } catch (err) {
      console.error('Error tracking usage:', err);
      throw err;
    }
  }, [userId, serviceInstance, loadPermissions]);

  const refresh = useCallback((): Promise<Permissions | null> => {
    if (!userId) return Promise.resolve(null);
    
    serviceInstance.clearCache(userId);
    return loadPermissions();
  }, [userId, serviceInstance, loadPermissions]);

  // Auto-load permissions
  useEffect(() => {
    if (!autoLoad || !userId || !serviceInstance) {
      setLoading(false); // ensure consistent state
      return;
    }
    loadPermissions();
  }, [autoLoad, userId, serviceInstance, loadPermissions]);

  // Refresh interval
  useEffect(() => {
    if (refreshInterval && userId) {
      intervalRef.current = setInterval(() => {
        refresh();
      }, refreshInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [refreshInterval, userId, refresh]);

  // Use useMemo for computed values to ensure consistent hook order
  const serviceUsage = useMemo((): ServiceUsage | null => {
    if (!permissions) return null;
    
    return {
      used: permissions.usage.servicesCount,
      total: permissions.limits.maxServices,
      remaining: permissions.limits.maxServices - permissions.usage.servicesCount,
      percentage: permissions.can.getServiceUsagePercentage()
    };
  }, [permissions]);

  const teamUsage = useMemo((): TeamUsage | null => {
    if (!permissions) return null;
    
    return {
      used: permissions.usage.teamMembersCount,
      total: permissions.limits.maxTeamMembers,
      remaining: permissions.limits.maxTeamMembers - permissions.usage.teamMembersCount,
      percentage: permissions.can.getTeamUsagePercentage()
    };
  }, [permissions]);

  // Quick access computed values
  const canAddService = useMemo(() => 
    permissions?.can.addService() || false, 
    [permissions]
  );

  const canAddTeamMember = useMemo(() => 
    permissions?.can.addTeamMember() || false, 
    [permissions]
  );

  const hasReachedServiceLimit = useMemo(() => 
    permissions?.can.hasReachedServiceLimit() || false, 
    [permissions]
  );

  const hasReachedTeamLimit = useMemo(() => 
    permissions?.can.hasReachedTeamLimit() || false, 
    [permissions]
  );

  return {
    // State
    permissions,
    loading,
    error,
    
    // Methods
    refresh,
    trackUsage,
    loadPermissions,
    
    // Convenience getters
    can: permissions?.can || emptyCanMethods,
    features: permissions?.features || emptyFeatures,
    usage: permissions?.usage || emptyUsage,
    subscription: permissions?.subscription || emptySubscription,
    limits: permissions?.limits || emptyLimits,
    addons: permissions?.addons || emptyAddons,
    
    // Enhanced usage info
    serviceUsage,
    teamUsage,
    
    // Quick access to common checks
    canAddService,
    canAddTeamMember,
    hasReachedServiceLimit,
    hasReachedTeamLimit
  };
};

export default usePermissions;